import React, { useCallback, useMemo, useRef, useState } from 'react';

import api from '../api';
import { AppSetting } from '../types';
import Context from './context';

interface Props {
    children: React.ReactElement;
}

const AppSettingsProvider = ({ children }: Props) => {
    const [settings, setSettings] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);
    const fetchPromiseRef = useRef<Promise<void> | null>(null);
    const loadedRef = useRef(false);

    const fetch = useCallback(() => {
        if (loadedRef.current) return Promise.resolve();
        if (fetchPromiseRef.current) return fetchPromiseRef.current;

        setLoading(true);
        fetchPromiseRef.current = api
            .getAll()
            .then((rows: AppSetting[]) => {
                const map: Record<string, any> = {};
                rows.forEach((r) => {
                    map[r.key] = r.value;
                });
                setSettings(map);
                loadedRef.current = true;
            })
            .catch(() => {})
            .finally(() => {
                setLoading(false);
                fetchPromiseRef.current = null;
            });
        return fetchPromiseRef.current;
    }, []);

    const update = useCallback(async (key: string, value: any) => {
        const result = await api.update(key, value);
        setSettings((prev) => ({ ...prev, [key]: result.value }));
        return result;
    }, []);

    const contextValue = useMemo(
        () => ({ settings, loading, fetch, update }),
        [settings, loading, fetch, update]
    );

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default AppSettingsProvider;
